/**
 * TenantAccessPanel — everything about HOW people reach and sign in to a
 * tenant, on one screen.
 *
 * The pieces used to be scattered: the sign-in hostname lived in the
 * White-Label editor's Basics group (it is not a brand setting — it decides
 * which URL resolves to this tenant), while the company and multi-user
 * switches sat alone on the Access tab with nothing around them. They belong
 * together, because they answer one question between them: who can sign in
 * here, and at what address.
 *
 * The hostname saves on its own — it is a column on the tenant row, not a
 * brand override, so it has no business riding the brand editor's save bar.
 *
 * Deliberately NOT built on the shared `.card` class: that one is a dashboard
 * tile (`flex: 1; overflow: hidden`), so inside a column it forces both panels
 * to equal height and then CLIPS whatever does not fit — which is exactly what
 * ate the end of the hostname explanation. A settings panel is as tall as its
 * content.
 */

import { useEffect, useMemo, useState } from 'react';
import { Button, Input, Tooltip, message } from 'antd';
import api from '@/services/api';
import { useLanguage } from '@/hooks/useLanguage';
import type { Tenant } from '@modules/super_admin/services/superAdminService';
import { CompanyTenantToggle, MultiUserLoginToggle } from './TenantAccessToggles';
import AccessRow from './AccessRow';
import '@modules/super_admin/console/console-shell.css';

interface Props {
  tenant: Tenant;
  onChanged: (updated: Tenant) => void;
}

export default function TenantAccessPanel({ tenant, onChanged }: Props) {
  const { t } = useLanguage();

  // `subdomain_url` is backend-computed (https://<subdomain>.corpbrain.com).
  // The column stores a HOSTNAME — it is matched against the Host header,
  // which carries no scheme — so strip it.
  const defaultHost = useMemo(
    () => ((tenant as unknown as { subdomain_url?: string }).subdomain_url ?? '').replace(/^https?:\/\//, ''),
    [tenant],
  );
  const savedHost = (tenant.tenant_domain ?? '') || defaultHost;

  const [host, setHost] = useState(savedHost);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setHost(savedHost); }, [savedHost]);

  const dirty = host.trim() !== savedHost.trim();

  // The real, openable URL for this tenant. Built from the running origin, so
  // it is correct in dev, staging and production without a hardcoded domain.
  const loginUrl = tenant.tenant_code
    ? `${window.location.origin}/${encodeURIComponent(tenant.tenant_code.toLowerCase())}/login`
    : null;

  const saveHost = async () => {
    const trimmed = host.trim();
    // Saving the computed default (or nothing) writes NULL: the
    // brand-from-host resolver already falls back to subdomain matching, so
    // storing the default would just be a redundant copy.
    const normalized = !trimmed || trimmed === defaultHost ? null : trimmed;
    setSaving(true);
    try {
      await api.put(`/super-admin/tenants/${tenant.tenant_id}`, { tenant_domain: normalized });
      onChanged({ ...tenant, tenant_domain: normalized });
      message.success(t('superAdmin.access.hostSaved'));
    } catch (err) {
      // 409 means another tenant already owns the hostname (a DB-level unique
      // constraint). Say which one it is rather than a generic failure.
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        message.error(t('superAdmin.access.hostTaken').replace('{{host}}', String(normalized)));
      } else {
        message.error(t('superAdmin.access.hostFailed'));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sa-access" data-testid="workspace-access">
      {/* ── Where people sign in ─────────────────────────────────────── */}
      <section className="sa-panel">
        <header className="sa-panel-head">
          <h3>{t('superAdmin.access.urlsTitle')}</h3>
          <p>{t('superAdmin.access.urlsSubtitle')}</p>
        </header>
        <div className="sa-panel-body">
          <AccessRow
            testId="access-row-login-url"
            label={t('superAdmin.access.loginUrlLabel')}
            hint={t('superAdmin.access.loginUrlHint')}
          >
            {loginUrl ? (
              <a
                className="sa-access-url"
                href={loginUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="tenant-login-link"
              >
                {loginUrl}
              </a>
            ) : (
              <span className="sa-access-hint">{t('superAdmin.access.noCode')}</span>
            )}
          </AccessRow>

          <AccessRow
            testId="access-row-custom-host"
            label={t('superAdmin.access.customHostLabel')}
            hint={t('superAdmin.access.customHostHint')}
          >
            <div className="sa-access-field">
              <Input
                value={host}
                placeholder={t('superAdmin.access.customHostPlaceholder')
                  .replace('{{host}}', defaultHost || 'login.acme.com')}
                onChange={e => setHost(e.target.value)}
                className="sa-access-input"
                data-testid="tenant-domain-input"
              />
              <Tooltip title={dirty ? '' : t('superAdmin.access.noChanges')}>
                {/* A disabled AntD button swallows pointer events, so the
                    tooltip explaining WHY it is disabled would never fire —
                    hence the wrapper span that keeps receiving them. */}
                <span>
                  <Button
                    type="primary"
                    disabled={!dirty}
                    loading={saving}
                    onClick={saveHost}
                    data-testid="tenant-domain-save"
                  >
                    {t('common.save')}
                  </Button>
                </span>
              </Tooltip>
            </div>
          </AccessRow>
        </div>
      </section>

      {/* ── Who can sign in ──────────────────────────────────────────── */}
      <section className="sa-panel">
        <header className="sa-panel-head">
          <h3>{t('superAdmin.access.policyTitle')}</h3>
          <p>{t('superAdmin.access.policySubtitle')}</p>
        </header>
        <div className="sa-panel-body">
          <CompanyTenantToggle tenant={tenant} onChanged={onChanged} />
          {/* Only meaningful on a company tenant; the toggle hides itself
              otherwise, so say why the space is empty rather than leaving a
              silent gap. */}
          {tenant.is_company ? (
            <MultiUserLoginToggle tenant={tenant} onChanged={onChanged} />
          ) : (
            <p className="sa-access-note">{t('superAdmin.access.multiUserNeedsCompany')}</p>
          )}
        </div>
      </section>
    </div>
  );
}
